import { Html } from '@react-three/drei';
import { type ThreeEvent } from '@react-three/fiber';
import debounce from 'debounce';
import { type FC, useCallback, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { DoubleSide } from 'three';
import type { PolygonProps, PolygonStyleDetail, PolygonStyleSpec } from '../../types';
import { ViewerLayer } from '../ViewerLayer';
import { RENDER_ORDER_POLYGON } from '../RenderOrder';

const defaultStyles: PolygonStyleSpec = {
  normal: {
    color: new THREE.Color(0.28, 0.75, 0.55),
    opacity: 0.4,
    size: 0.3,
    emissiveIntensity: 0.1,
  },
  highlighted: {
    color: new THREE.Color(1, 0.05, 0.05),
    opacity: 0.6,
    size: 0.3,
    emissiveIntensity: 0.3,
  },
};

export const Polygon: FC<PolygonProps> = ({
  borderPoints = [],
  styles,
  name,
  isVisible,
  onClicked,
}) => {
  const [isHighlighted, setIsHighlighted] = useState(false);
  const mesh = useRef<THREE.Mesh>();

  /*
   points in the array are expected to be ordered
   eg:
      '1     '2     '3
   '8                     '4


       '7               '5
                  '6
  */

  const centerPoint = useMemo(() => {
    const { length } = borderPoints;
    return borderPoints.reduce(
      (accumulator, current, currentIndex) => {
        // Add x,y,z value up
        accumulator.x += current.x;
        accumulator.y += current.y;
        accumulator.z += current.z;

        // If is last point set in the array, return average
        if (currentIndex === length - 1) {
          return {
            x: accumulator.x / length,
            y: accumulator.y / length,
            z: accumulator.z / length,
          };
        }

        // otherwise return sum
        return accumulator;
      },
      { x: 0, y: 0, z: 0 }
    );
  }, [borderPoints]);

  /*
   Because buffer geometry only draws triangle, so need to make points path
   starting from center point
      '1     '2,4     '5
   '                     '
              '0,3

       '               '
                  '
   trying to draw triangles 0->1->2, 3->4->5, ...
   */

  /* Potential optimization:
      from Stan: thought:
      Could set points directly into Float32Array to avoid copying from renderArray to new Float32Array (performance gain).
      Probably it's not an issue, since polygon array is small (I guess less than 1000 points).

      *** Currently draw only few polygons at a time

      ref: https://github.com/abyss-solutions/abyss-3d-viewer/pull/120#discussion_r967931910
   */
  const vertices = useMemo(() => {
    const renderArray: number[][] = [];
    borderPoints.forEach((point, index) => {
      const nextPoint =
        index === borderPoints.length - 1 ? borderPoints[0] : borderPoints[index + 1];
      renderArray.push(
        Object.values(centerPoint),
        [point.x, point.y, point.z],
        [nextPoint.x, nextPoint.y, nextPoint.z]
      );
    });
    // bufferGeometry only accepts float32 array
    return new Float32Array(renderArray.flat());
  }, [borderPoints, centerPoint]);

  const meshCallback = useCallback(
    (node: THREE.Mesh | null) => {
      if (node != null) {
        const { layers } = node;

        if (isVisible ?? true) {
          layers.enable(ViewerLayer.AutoPickable);
          layers.enable(ViewerLayer.Default);
          layers.enable(ViewerLayer.SphericalViewerLayer);
        } else {
          layers.disable(ViewerLayer.AutoPickable);
          layers.disable(ViewerLayer.Default);
          layers.disable(ViewerLayer.SphericalViewerLayer);
        }

        mesh.current = node;
      }
    },
    [isVisible]
  );

  const isClosestToCursor = useCallback((event: ThreeEvent<MouseEvent>): boolean => {
    // prevents this spherical from being selected if it is behind any meshes from the cameras point of view
    const { intersections } = event;
    return mesh.current === intersections[0].object;
  }, []);

  const handlePointerOver = (event: ThreeEvent<MouseEvent>) => {
    if (isClosestToCursor(event)) {
      setIsHighlighted(true);
    }
  };

  const handlePointerOut = () => {
    setIsHighlighted(false);
  };

  const handleClick = useCallback(
    (event: ThreeEvent<MouseEvent>) => {
      if (isClosestToCursor(event)) {
        // Ignore if it's a double click,
        if (event.nativeEvent.detail === 2) {
          return;
        }
        setIsHighlighted(false);
        if (onClicked) {
          onClicked({ centerPoint, polygonCoordinates: borderPoints });
        }
      }
    },
    [centerPoint, onClicked, borderPoints, isClosestToCursor]
  );

  const debouncedHandleClick = debounce(handleClick, 300);

  const actualStyle: PolygonStyleDetail = useMemo(
    () =>
      isHighlighted
        ? (styles?.highlighted ?? defaultStyles.highlighted!)
        : (styles?.normal ?? defaultStyles.normal!),
    [isHighlighted, styles?.highlighted, styles?.normal]
  );

  const displayName = name.includes('/') ? name.split('/').pop() : name;

  return (
    <group position={[0, 0, 0]}>
      {/* {isSphericalViewMode && ( */}
      <mesh
        ref={meshCallback}
        position={[0, 0, 0]}
        onPointerOver={handlePointerOver}
        onPointerOut={handlePointerOut}
        onClick={debouncedHandleClick}
        renderOrder={RENDER_ORDER_POLYGON}
      >
        {isHighlighted && (
          <Html
            position={[centerPoint.x + 0.2, centerPoint.y + 0.2, centerPoint.z + 0.2]}
            as="div"
            center
            zIndexRange={[100, 0]}
            style={{ pointerEvents: 'none' }}
          >
            <div style={{ backgroundColor: 'white', padding: '4px 8px', borderRadius: '4px' }}>
              <span style={{ color: 'black', userSelect: 'all' }}>{displayName}</span>
            </div>
          </Html>
        )}
        <bufferGeometry attach="geometry">
          <bufferAttribute
            attach="attributes-position"
            array={vertices}
            itemSize={3}
            count={vertices.length / 3}
          />
        </bufferGeometry>
        <meshBasicMaterial
          transparent
          attach="material"
          color={actualStyle.color}
          opacity={actualStyle.opacity}
          side={DoubleSide}
        />
      </mesh>
      {/* )} */}
    </group>
  );
};
